import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PhoneCall, Tv } from 'lucide-react';
import type { CliActionResult, OnuIptvState, OnuVoipState } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useTasks } from '../../lib/tasks';
import { Button, Card, ErrorNotice, Field, Input, Pill, Spinner } from '../../components/ui';
import { VoipCard } from './ConfigSections';

/**
 * The ONU page's VoIP and IPTV tabs (operator, 2026-09-26).
 *
 * Both switch a SERVICE on the OLT for one ONU — `POST /olts/:id/onus/:onuId/voip`
 * and `…/iptv` — and read back what this platform last set (no chassis read
 * reports either, so the page says "last set from SimpleACS", never "on").
 * Every write goes straight to the chassis (no command preview — the platform's
 * rule) and runs in the tasks tray, so the tab never pins the operator to a
 * spinner and a chassis refusal stays until it is read.
 *
 * What gates a write is said where the control is: the family refusal (a
 * C650), the per-unit one (a model with no phone socket), and — on a Huawei
 * OLT — that the write is SENT UNVERIFIED, composed from the vendor reference
 * and never yet run on one.
 */

const enc = encodeURIComponent;

/**
 * A server sentence with its `command` spans set as code — the refusal and
 * unverified sentences quote chassis verbs in backticks, and raw backticks
 * read as noise on a page.
 */
function Sentence({ text }: { text: string }) {
  return (
    <>
      {text.split('`').map((part, i) =>
        i % 2 === 1 ? (
          <code key={i} className="font-mono">
            {part}
          </code>
        ) : (
          part
        ),
      )}
    </>
  );
}

function Refusal({ text }: { text: string }) {
  return (
    <p className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
      <Sentence text={text} />
    </p>
  );
}

function Unverified({ text }: { text: string }) {
  return (
    <p className="rounded-lg border border-info/30 bg-info-soft px-3 py-2 text-xs text-info">
      <Sentence text={text} />
    </p>
  );
}

/** 1…n, the sockets a model has. */
const sockets = (n: number) => Array.from({ length: n }, (_, i) => i + 1);

/** Tick-box row over a model's sockets; all ticked by default. */
function SocketPicker({
  prefix,
  count,
  chosen,
  onChange,
}: {
  prefix: string;
  count: number;
  chosen: number[];
  onChange: (next: number[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {sockets(count).map((n) => (
        <label key={n} className="flex items-center gap-1.5 text-sm text-ink">
          <input
            type="checkbox"
            checked={chosen.includes(n)}
            onChange={(e) =>
              onChange(e.target.checked ? [...chosen, n].sort((a, b) => a - b) : chosen.filter((p) => p !== n))
            }
          />
          <span className="font-mono text-xs">
            {prefix}
            {n}
          </span>
        </label>
      ))}
    </div>
  );
}

// --- VoIP --------------------------------------------------------------------

export function VoipTab({
  oltId,
  onuId,
  serial,
  vendor,
  deviceId,
}: {
  oltId: string;
  onuId: string;
  serial: string;
  vendor: 'zte' | 'huawei';
  deviceId: string | null;
}) {
  const { can } = useSession();
  const tasks = useTasks();
  const key = ['onu-voip', oltId, onuId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<OnuVoipState>(`/olts/${enc(oltId)}/onus/${enc(onuId)}/voip`),
  });
  const [ports, setPorts] = useState<number[]>([]);
  const [profile, setProfile] = useState('');
  const count = q.data?.potsPorts ?? null;
  // Every socket the model has, once the model is known.
  useEffect(() => {
    if (count && count > 0) setPorts(sockets(count));
  }, [count]);

  const state = q.data;
  const writable = can('onu.provision') && state && !state.refusal;

  const run = (enabled: boolean) =>
    tasks.start({
      label: `VoIP ${enabled ? 'on' : 'off'} ${serial}`,
      run: () =>
        api.post<CliActionResult>(`/olts/${enc(oltId)}/onus/${enc(onuId)}/voip`, {
          enabled,
          protocol: 'sip',
          ...(count && count > 0 ? { potsPorts: ports } : {}),
          ...(enabled && vendor === 'zte' && profile.trim() ? { profile: profile.trim() } : {}),
          dryRun: false,
        }),
      done: () =>
        `VoIP ${enabled ? 'on' : 'off'} for ${serial}${state?.unverified ? ' — sent unverified on this OLT' : ''}.`,
      invalidate: [key],
    });

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <PhoneCall className="size-4 text-muted" />
            VoIP on the OLT
          </span>
        }
      >
        <div className="space-y-4 p-5">
          {q.isLoading && <Spinner />}
          {q.error && <ErrorNotice error={q.error} />}
          {state && (
            <>
              <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
                <div className="flex items-center justify-between gap-3 sm:justify-start">
                  <dt className="text-muted">Last set from SimpleACS</dt>
                  <dd>
                    {state.enabled === null ? (
                      <Pill tone="neutral">never</Pill>
                    ) : state.enabled ? (
                      <Pill tone="success" dot>
                        on — SIP
                      </Pill>
                    ) : (
                      <Pill tone="neutral" dot>
                        off
                      </Pill>
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3 sm:justify-start">
                  <dt className="text-muted">Phone sockets</dt>
                  <dd className="text-ink">
                    {count === null ? 'not recorded for this model' : count === 0 ? 'none on this model' : `${count} (pots_0/1${count > 1 ? `–${count}` : ''})`}
                  </dd>
                </div>
              </dl>
              <p className="text-xs text-faint">
                {vendor === 'zte'
                  ? 'The OLT switches the ONU’s phone protocol and binds each socket to a SIP profile the OLT already holds.'
                  : 'The OLT switches each phone socket on or off. The SIP account (server, number, credentials) is a profile on the ONT or on the ACS and is not written here.'}{' '}
                No chassis read reports whether VoIP is switched on, so this shows what SimpleACS last set.
              </p>
              {state.refusal && <Refusal text={state.refusal} />}
              {!state.refusal && state.unverified && <Unverified text={state.unverified} />}

              {writable && (
                <div className="space-y-3 rounded-lg border border-line bg-surface-2 p-4">
                  {count !== null && count > 0 && (
                    <Field label="Sockets">
                      <SocketPicker prefix="pots_0/" count={count} chosen={ports} onChange={setPorts} />
                    </Field>
                  )}
                  {vendor === 'zte' && (
                    <Field
                      label="SIP profile (optional)"
                      hint="A profile the OLT already holds, by its name there. Empty switches the protocol only."
                    >
                      <Input
                        value={profile}
                        onChange={(e) => setProfile(e.target.value)}
                        maxLength={32}
                        className="max-w-xs"
                        placeholder="e.g. sip_profile"
                      />
                    </Field>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={count !== null && count > 0 && ports.length === 0}
                      onClick={() => run(true)}
                    >
                      Switch VoIP on
                    </Button>
                    <Button
                      size="sm"
                      disabled={count !== null && count > 0 && ports.length === 0}
                      onClick={() => run(false)}
                    >
                      Switch VoIP off
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      <Card title="SIP lines on the router">
        {deviceId ? (
          <VoipCard deviceId={deviceId} />
        ) : (
          <p className="p-5 text-sm text-muted">
            {serial} has never informed an ACS, so its SIP registration cannot be read. The OLT-side
            switch above works regardless.
          </p>
        )}
      </Card>
    </div>
  );
}

// --- IPTV --------------------------------------------------------------------

export function IptvTab({
  oltId,
  oltHref,
  onuId,
  serial,
  vendor,
}: {
  oltId: string;
  /** The OLT's own page, for "mark the multicast VLAN there". */
  oltHref: string;
  onuId: string;
  serial: string;
  vendor: 'zte' | 'huawei';
}) {
  const { can } = useSession();
  const tasks = useTasks();
  const key = ['onu-iptv', oltId, onuId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<OnuIptvState>(`/olts/${enc(oltId)}/onus/${enc(onuId)}/iptv`),
  });
  const state = q.data;
  const [vlan, setVlan] = useState('');
  const [ports, setPorts] = useState<number[]>([]);
  const [servicePortVlan, setServicePortVlan] = useState('');
  const eth = state?.ethPorts ?? null;

  // The VLAN the unit carries now, else the one the OLT marks for IPTV.
  const current = state?.vlanId ?? null;
  const marked = state?.oltIptvVlans.join(',') ?? '';
  useEffect(() => {
    const only = marked !== '' && !marked.includes(',') ? Number(marked) : null;
    const pick = current ?? only;
    setVlan(pick === null ? '' : String(pick));
  }, [current, marked]);
  useEffect(() => {
    if (eth && eth > 0) setPorts(sockets(eth));
  }, [eth]);

  const writable = can('onu.provision') && state && !state.refusal;
  // A VLAN is picked from the OLT's own IPTV-marked VLANs — plus the one already on the unit.
  const choices = state ? [...new Set([...(state.vlanId !== null ? [state.vlanId] : []), ...state.oltIptvVlans])] : [];
  const spv = servicePortVlan.trim();
  const spvValid = spv === '' || (/^\d+$/.test(spv) && Number(spv) >= 1 && Number(spv) <= 4094);

  const run = (enabled: boolean) =>
    tasks.start({
      label: `IPTV ${enabled ? 'on' : vendor === 'zte' ? 'pause' : 'off'} ${serial}`,
      run: () =>
        api.post<CliActionResult>(`/olts/${enc(oltId)}/onus/${enc(onuId)}/iptv`, {
          enabled,
          ...(vlan ? { vlanId: Number(vlan) } : {}),
          ...(enabled && vendor === 'zte' && eth && eth > 0 ? { ethPorts: ports } : {}),
          ...(vendor === 'huawei' && spv ? { servicePortVlan: Number(spv) } : {}),
          dryRun: false,
        }),
      done: () =>
        `IPTV ${enabled ? `on for ${serial} (VLAN ${vlan})` : `${vendor === 'zte' ? 'paused' : 'off'} for ${serial}`}${
          state?.unverified ? ' — sent unverified on this OLT' : ''
        }.`,
      invalidate: [key],
    });

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Tv className="size-4 text-muted" />
          IPTV on the OLT
        </span>
      }
    >
      <div className="space-y-4 p-5">
        {q.isLoading && <Spinner />}
        {q.error && <ErrorNotice error={q.error} />}
        {state && (
          <>
            <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
              <div className="flex items-center justify-between gap-3 sm:justify-start">
                <dt className="text-muted">Last set from SimpleACS</dt>
                <dd>
                  {state.enabled === null ? (
                    <Pill tone="neutral">never</Pill>
                  ) : state.enabled ? (
                    <Pill tone="success" dot>
                      on — VLAN {state.vlanId}
                    </Pill>
                  ) : (
                    <Pill tone="neutral" dot>
                      {vendor === 'zte' ? 'paused' : 'off'} — VLAN {state.vlanId}
                    </Pill>
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 sm:justify-start">
                <dt className="text-muted">Ethernet sockets</dt>
                <dd className="text-ink">{eth === null ? 'not recorded for this model' : eth}</dd>
              </div>
            </dl>
            <p className="text-xs text-faint">
              {vendor === 'zte'
                ? 'The OLT starts its IPTV service for this ONU and the unit strips the multicast VLAN onto the chosen sockets with IGMP on each. Pausing stops the OLT-side service and leaves the unit’s lines, so turning it back on needs no rebuild.'
                : 'The subscriber’s service-port joins the multicast VLAN and becomes an IGMP user. The VLAN’s IGMP mode is the OLT’s own setting and is not changed.'}{' '}
              No chassis read reports IPTV, so this shows what SimpleACS last set.
            </p>
            {state.refusal && <Refusal text={state.refusal} />}
            {!state.refusal && state.unverified && <Unverified text={state.unverified} />}

            {writable && choices.length === 0 && (
              <p className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-muted">
                No VLAN on this OLT is marked for IPTV. Mark the multicast VLAN as used for IPTV on the
                OLT&rsquo;s{' '}
                <Link to={`${oltHref}?tab=vlans`} className="text-link hover:underline">
                  VLANs tab
                </Link>
                , then come back.
              </p>
            )}

            {writable && choices.length > 0 && (
              <div className="space-y-3 rounded-lg border border-line bg-surface-2 p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Multicast VLAN" hint="The OLT's VLANs marked for IPTV.">
                    <select className="input max-w-xs" value={vlan} onChange={(e) => setVlan(e.target.value)}>
                      {vlan === '' && <option value="">Choose a VLAN</option>}
                      {choices.map((v) => (
                        <option key={v} value={String(v)}>
                          VLAN {v}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {vendor === 'huawei' && (
                    <Field
                      label="Subscriber service-port VLAN (optional)"
                      hint="Only when the ONT carries more than one subscriber service-port."
                      error={spvValid ? undefined : 'A VLAN is a number from 1 to 4094.'}
                    >
                      <Input
                        value={servicePortVlan}
                        onChange={(e) => setServicePortVlan(e.target.value)}
                        inputMode="numeric"
                        className="max-w-[10rem]"
                        placeholder="e.g. 5"
                      />
                    </Field>
                  )}
                </div>
                {vendor === 'zte' && eth !== null && eth > 0 && (
                  <Field label="Sockets">
                    <SocketPicker prefix="eth_0/" count={eth} chosen={ports} onChange={setPorts} />
                  </Field>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!vlan || !spvValid || (vendor === 'zte' && eth !== null && eth > 0 && ports.length === 0)}
                    onClick={() => run(true)}
                  >
                    Switch IPTV on
                  </Button>
                  <Button size="sm" disabled={!vlan || !spvValid} onClick={() => run(false)}>
                    {vendor === 'zte' ? 'Pause IPTV' : 'Switch IPTV off'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
